import dayjs from 'dayjs';
import { FC, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from 'shared/components/Button.tsx';
import { AngleIcon, BritishFlagIcon, PolishFlagIcon } from 'shared/icons';
import { IconType } from 'shared/icons/SvgIcon.tsx';
import { mergeClasses } from 'shared/utils/mergeClasses.ts';

const availableLanguages = ['en', 'pl'] as const;

const optionIcons: Record<string, IconType> = {
  pl: PolishFlagIcon,
  en: BritishFlagIcon,
};

interface LanguageSwitchProps {
  className?: string;
}

export const LanguageSwitch: FC<LanguageSwitchProps> = ({ className = '' }) => {
  const [open, setOpen] = useState(false);

  const { t, i18n } = useTranslation();
  const { language, changeLanguage } = i18n;
  const currentLanguage = useMemo(
    () => (language.includes('-') ? language.split('-')[0] : language),
    [language],
  );

  const options = useMemo(
    () =>
      availableLanguages.map((lang) => ({
        value: lang,
        icon: optionIcons[lang],
        label: t(`dictionaries:languages.${lang}`),
      })),
    [t],
  );
  const { label: currentLangLabel, icon: CurrentIcon } = useMemo(
    () =>
      options.find(({ value }) => value === currentLanguage) ?? {
        label: '-',
        icon: null,
      },
    [currentLanguage, options],
  );

  const handleSelectLanguage = useCallback(async (lang: (typeof availableLanguages)[number]) => {
    await changeLanguage(lang);
    setOpen(false);
    dayjs.locale(lang);
  }, []);

  return (
    <div className={mergeClasses('relative z-10', className)}>
      <Button
        onClick={() => setOpen(true)}
        variant="text"
        className="gap-2 whitespace-nowrap text-sm font-medium !text-white sm:!text-primary"
      >
        {CurrentIcon && (
          <span className="hidden overflow-hidden rounded-sm border border-gray-200 sm:inline-block">
            <CurrentIcon className="h-3 w-4" />
          </span>
        )}
        <p className="flex gap-1">
          <span className="hidden sm:inline">{currentLangLabel}</span>
          <span className="uppercase">{`(${currentLanguage})`}</span>
        </p>
        <AngleIcon className={mergeClasses('h-6 w-6 transition-all', open ? 'rotate-180' : '')} />
      </Button>
      {open && (
        <>
          {/* eslint-disable-next-line jsx-a11y/control-has-associated-label */}
          <button
            tabIndex={-1}
            onClick={() => setOpen(false)}
            className="fixed left-0 top-0 z-20 h-screen w-screen"
          />
          <div className="absolute bottom-[calc(100%+6px)] left-0 z-30 w-[200px] rounded-lg bg-white/95 py-4 backdrop-blur-2xl sm:bottom-auto sm:left-auto sm:right-0 sm:top-[calc(100%+6px)] sm:bg-black/15">
            <ul>
              {options.map(({ value, label, icon: Icon }) => (
                <li key={value}>
                  <button
                    disabled={value === currentLanguage}
                    onClick={() => handleSelectLanguage(value)}
                    className="flex w-full items-center gap-2 space-x-2 p-2 px-4 text-sm transition-all enabled:cursor-pointer enabled:hover:bg-black/20 disabled:bg-black/20 disabled:opacity-50"
                  >
                    {Icon && <Icon className="h-3 w-4" />}
                    <p className="flex gap-1">
                      <span>{label}</span>
                      <span className="uppercase">{`(${value})`}</span>
                    </p>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </>
      )}
    </div>
  );
};
